import { useState, useEffect, useCallback, useRef } from 'react';

interface UseAdminInactivityTimeoutOptions {
  timeoutMs: number; // Total timeout before logout (e.g., 30 min = 1800000)
  warningMs: number; // Time before timeout to show warning (e.g., 2 min = 120000)
  onTimeout: () => void;
}

export function useAdminInactivityTimeout({
  timeoutMs,
  warningMs,
  onTimeout,
}: UseAdminInactivityTimeoutOptions) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const resetTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    lastActivityRef.current = Date.now();

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSeconds(Math.floor(warningMs / 1000));

      // Start countdown
      countdownRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Set logout timer
      logoutTimerRef.current = setTimeout(() => {
        onTimeout();
      }, warningMs);
    }, timeoutMs - warningMs);
  }, [timeoutMs, warningMs, onTimeout, clearAllTimers]);

  const handleActivity = useCallback(() => {
    // Only reset if warning is not showing (to avoid resetting during warning)
    if (!showWarning) {
      lastActivityRef.current = Date.now();
    }
  }, [showWarning]);

  const extendSession = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetTimers();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [handleActivity, resetTimers, clearAllTimers]);

  return {
    showWarning,
    remainingSeconds,
    extendSession,
  };
}
